import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { notUndefinedAndNull } from '@helpers/array';
import { isValidNumber } from '@helpers/utils';

import { getHotelMetaSearchState, getPriceOptionsState } from '@redux/selectors';

import { HotelMetaSearch } from '@wegoTypes/hotels/hotelMetaSearch';

import { useReviewScoreManager } from './useReviewScoreManager';

export const useFilterToggle = () => {
  const hotelMetaSearch: HotelMetaSearch = useSelector(getHotelMetaSearchState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const locationState = state as {
    impressionType: 'sort' | 'filter' | 'page_load' | 'map_load';
    eventTrackingObj?: Record<string, any>;
  };
  const { reviewScoreRanges, selectedRangeIds, onReviewScoreToggle } = useReviewScoreManager();

  const {
    starsFilter: stars,
    amenitiesFilter: amenities,
    districtsFilter: districts,
    propertyTypesFilter: propertyTypes,
    reviewerGroupsFilter: reviewerGroups,
    rateAmenitiesFilter: rateAmenities,
    brandsFilter: brands,
    chainsFilter: chains,
  } = hotelMetaSearch;

  const pricesSearchParam = searchParams.get('price');
  const starIdsSearchParam = searchParams.get('stars');
  const amenityIdsSearchParam = searchParams.get('amenities');
  const districtIdsSearchParam = searchParams.get('districts');
  const propertyTypeIdsSearchParam = searchParams.get('accommodation_types');
  const reviewerGroupsSearchParam = searchParams.get('guest_rating');
  const rateAmenityIdsSearchParam = searchParams.get('free_amenities');
  const chainIdsSearchParam = searchParams.get('chains');
  const brandIdsSearchParam = searchParams.get('brands');

  const usdPrices = useMemo(() => {
    const allPrices: number[] = [];

    hotelMetaSearch.hotelViewModels.forEach((hotelViewModel) => {
      const rates = hotelViewModel.rates;
      if (rates.length === 0) return;

      // Single loop to process all rates for this hotel
      rates.forEach((rate) => {
        if (!!rate) {
          const { taxInclusive, amountUsd, totalAmountUsd, taxAmountUsd, totalTaxAmountUsd } =
            rate.price;
          let calculatedAmountUsd: number = 0;

          let toBeCalculatedAmountUsd = taxInclusive ? amountUsd - taxAmountUsd : amountUsd;
          let toBeCalculatedTaxAmountUsd = taxAmountUsd;

          if (priceOptions.showTotalPrice) {
            toBeCalculatedAmountUsd = taxInclusive
              ? totalAmountUsd - totalTaxAmountUsd
              : totalAmountUsd;
            toBeCalculatedTaxAmountUsd = totalTaxAmountUsd;
          }

          if (priceOptions.isIncludingTaxOrFee) {
            calculatedAmountUsd = toBeCalculatedAmountUsd + toBeCalculatedTaxAmountUsd;
          } else {
            calculatedAmountUsd = toBeCalculatedAmountUsd;
          }

          allPrices.push(calculatedAmountUsd);
        }
      });
    });
    return allPrices.filter(notUndefinedAndNull);
  }, [hotelMetaSearch.hotelViewModels, priceOptions]);

  const minUsdPrice = usdPrices.length ? Math.floor(Math.min(...usdPrices)) : 0;
  const maxUsdPrice = usdPrices.length ? Math.ceil(Math.max(...usdPrices)) : 0;

  const selectedUsdPrices = useMemo<Array<number>>(() => {
    if (!!pricesSearchParam) {
      return pricesSearchParam.split(',').map((price) => parseInt(price));
    } else {
      if (isValidNumber(minUsdPrice) && isValidNumber(maxUsdPrice)) {
        return [minUsdPrice, maxUsdPrice];
      }
      return [];
    }
  }, [minUsdPrice, maxUsdPrice, pricesSearchParam]);

  const selectedStarIds = useMemo<Array<number>>(() => {
    if (!!starIdsSearchParam) {
      return starIdsSearchParam.split(',').map((starId) => parseInt(starId));
    }
    return [];
  }, [starIdsSearchParam]);

  const selectedAmenityIds = useMemo<Array<number>>(() => {
    if (!!amenityIdsSearchParam) {
      return amenityIdsSearchParam.split(',').map((amenityId) => parseInt(amenityId));
    }
    return [];
  }, [amenityIdsSearchParam]);

  const selectedDistrictIds = useMemo<Array<number>>(() => {
    if (!!districtIdsSearchParam) {
      return districtIdsSearchParam.split(',').map((districtId) => parseInt(districtId));
    }
    return [];
  }, [districtIdsSearchParam]);

  const selectedPropertyTypeIds = useMemo<Array<number>>(() => {
    if (!!propertyTypeIdsSearchParam) {
      return propertyTypeIdsSearchParam
        .split(',')
        .map((propertyTypeId) => parseInt(propertyTypeId));
    }
    return [];
  }, [propertyTypeIdsSearchParam]);

  const selectedReviewerGroups = useMemo<Array<string>>(() => {
    if (!!reviewerGroupsSearchParam) {
      return reviewerGroupsSearchParam.toUpperCase().split(',');
    }
    return [];
  }, [reviewerGroupsSearchParam]);

  const selectedRateAmenityIds = useMemo<Array<number>>(() => {
    if (!!rateAmenityIdsSearchParam) {
      return rateAmenityIdsSearchParam.split(',').map((rateAmenityId) => parseInt(rateAmenityId));
    }
    return [];
  }, [rateAmenityIdsSearchParam]);

  const selectedChainIds = useMemo<Array<number>>(() => {
    if (!!chainIdsSearchParam) {
      return chainIdsSearchParam.split(',').map((chainId) => parseInt(chainId));
    }
    return [];
  }, [chainIdsSearchParam]);

  const selectedBrandIds = useMemo<Array<number>>(() => {
    if (!!brandIdsSearchParam) {
      return brandIdsSearchParam.split(',').map((brandId) => parseInt(brandId));
    }
    return [];
  }, [brandIdsSearchParam]);

  const onStarToggle = useCallback<(starId: number, isSelected: boolean) => void>(
    (starId: number, isSelected: boolean) => {
      const cloneSelectedStarIds = [...selectedStarIds];

      if (isSelected) {
        cloneSelectedStarIds.push(starId);
      } else {
        cloneSelectedStarIds.splice(cloneSelectedStarIds.indexOf(starId), 1);
      }

      searchParams.delete('stars');

      if (cloneSelectedStarIds.length > 0) {
        searchParams.set('stars', cloneSelectedStarIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              stars: cloneSelectedStarIds.sort(),
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const onAmenityToggle = useCallback<(amenityId: number, isSelected: boolean) => void>(
    (amenityId: number, isSelected: boolean) => {
      const cloneSelectedAmenityIds = [...selectedAmenityIds];

      if (isSelected) {
        cloneSelectedAmenityIds.push(amenityId);
      } else {
        cloneSelectedAmenityIds.splice(cloneSelectedAmenityIds.indexOf(amenityId), 1);
      }
      const selectedAmenitiesName = cloneSelectedAmenityIds.map(
        (selectedAmenity) => amenities.find((amenity) => amenity.id === selectedAmenity)?.name,
      );
      searchParams.delete('amenities');

      if (cloneSelectedAmenityIds.length > 0) {
        searchParams.set('amenities', cloneSelectedAmenityIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              amenities: selectedAmenitiesName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState, amenities],
  );

  const onDistrictToggle = useCallback<(districtId: number, isSelected: boolean) => void>(
    (districtId: number, isSelected: boolean) => {
      const cloneSelectedDistrictIds = [...selectedDistrictIds];

      if (isSelected) {
        cloneSelectedDistrictIds.push(districtId);
      } else {
        cloneSelectedDistrictIds.splice(cloneSelectedDistrictIds.indexOf(districtId), 1);
      }

      searchParams.delete('districts');

      if (cloneSelectedDistrictIds.length > 0) {
        searchParams.set('districts', cloneSelectedDistrictIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              districts: cloneSelectedDistrictIds,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const onPropertyTypeToggle = useCallback<(propertyTypeId: number, isSelected: boolean) => void>(
    (propertyTypeId: number, isSelected: boolean) => {
      const cloneSelectedPropertyTypeIds = [...selectedPropertyTypeIds];

      if (isSelected) {
        cloneSelectedPropertyTypeIds.push(propertyTypeId);
      } else {
        cloneSelectedPropertyTypeIds.splice(
          cloneSelectedPropertyTypeIds.indexOf(propertyTypeId),
          1,
        );
      }
      const selectedPropertiesTypeName = cloneSelectedPropertyTypeIds.map(
        (typeId) => propertyTypes.find((property) => property.id === typeId)?.name,
      );
      searchParams.delete('accommodation_types');

      if (cloneSelectedPropertyTypeIds.length > 0) {
        searchParams.set('accommodation_types', cloneSelectedPropertyTypeIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              property_types: selectedPropertiesTypeName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState, propertyTypes],
  );

  const onReviewerGroupToggle = useCallback<(reviewerGroup: string, isSelected: boolean) => void>(
    (reviewerGroup: string, isSelected: boolean) => {
      const cloneSelectedReviewerGroups = [...selectedReviewerGroups];

      if (isSelected) {
        cloneSelectedReviewerGroups.push(reviewerGroup);
      } else {
        cloneSelectedReviewerGroups.splice(cloneSelectedReviewerGroups.indexOf(reviewerGroup), 1);
      }

      searchParams.delete('guest_rating');

      if (cloneSelectedReviewerGroups.length > 0) {
        searchParams.set('guest_rating', cloneSelectedReviewerGroups.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              review_groups: cloneSelectedReviewerGroups,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const onRateAmenityToggle = useCallback<(rateAmenityId: number, isSelected: boolean) => void>(
    (rateAmenityId: number, isSelected: boolean) => {
      const cloneSelectedRateAmenityIds = [...selectedRateAmenityIds];

      if (isSelected) {
        cloneSelectedRateAmenityIds.push(rateAmenityId);
      } else {
        cloneSelectedRateAmenityIds.splice(cloneSelectedRateAmenityIds.indexOf(rateAmenityId), 1);
      }
      const selectedRateAmenityName = cloneSelectedRateAmenityIds.map(
        (selectedRateAmenityId) =>
          rateAmenities.find((rateAmenity) => rateAmenity.id === selectedRateAmenityId)?.name,
      );
      searchParams.delete('free_amenities');

      if (cloneSelectedRateAmenityIds.length > 0) {
        searchParams.set('free_amenities', cloneSelectedRateAmenityIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              rate_types: selectedRateAmenityName,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState, rateAmenities],
  );

  const onChainToggle = useCallback<(chainId: number, isSelected: boolean) => void>(
    (chainId: number, isSelected: boolean) => {
      const cloneSelectedChainIds = [...selectedChainIds];

      if (isSelected) {
        cloneSelectedChainIds.push(chainId);
      } else {
        cloneSelectedChainIds.splice(cloneSelectedChainIds.indexOf(chainId), 1);
      }

      searchParams.delete('chains');

      if (cloneSelectedChainIds.length > 0) {
        searchParams.set('chains', cloneSelectedChainIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              chains: cloneSelectedChainIds,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const onBrandToggle = useCallback<(brandId: number, isSelected: boolean) => void>(
    (brandId: number, isSelected: boolean) => {
      const cloneSelectedBrandIds = [...selectedBrandIds];

      if (isSelected) {
        cloneSelectedBrandIds.push(brandId);
      } else {
        cloneSelectedBrandIds.splice(cloneSelectedBrandIds.indexOf(brandId), 1);
      }

      searchParams.delete('brands');

      if (cloneSelectedBrandIds.length > 0) {
        searchParams.set('brands', cloneSelectedBrandIds.join(','));
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            eventTrackingObj: {
              ...locationState.eventTrackingObj,
              brands: cloneSelectedBrandIds,
            },
            impressionType: 'filter',
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const onUsdPricesChanged = useCallback<(usdPrices: Array<number>) => void>(
    (usdPrices: Array<number>) => {
      let eventTrackingObj: {
        price_min_max_usd: number[];
        price_selected_usd: number[];
      } = {
        price_min_max_usd: [],
        price_selected_usd: [],
      };
      searchParams.delete('price');

      if (
        isValidNumber(minUsdPrice) &&
        isValidNumber(maxUsdPrice) &&
        !(usdPrices[0] === minUsdPrice && usdPrices[1] === maxUsdPrice)
      ) {
        searchParams.set('price', usdPrices.join(','));
        eventTrackingObj = {
          price_min_max_usd: [minUsdPrice, maxUsdPrice],
          price_selected_usd: usdPrices,
        };
      }
      navigate(
        { pathname, search: searchParams.toString() },
        {
          replace: true,
          state: {
            ...locationState,
            impressionType: 'filter',
            ...(isValidNumber(minUsdPrice) &&
              isValidNumber(maxUsdPrice) && {
                eventTrackingObj: {
                  ...locationState.eventTrackingObj,
                  ...eventTrackingObj,
                },
              }),
          },
        },
      );
    },
    [pathname, search, locationState],
  );

  const selectedFilters = useMemo(() => {
    const filterMappings = [
      {
        data: reviewScoreRanges,
        selectedIds: selectedRangeIds,
        key: 'guest_score' as const,
        callback: onReviewScoreToggle,
      },
      { data: stars, selectedIds: selectedStarIds, key: 'stars' as const, callback: onStarToggle },
      {
        data: amenities,
        selectedIds: selectedAmenityIds,
        key: 'amenities' as const,
        callback: onAmenityToggle,
      },
      {
        data: districts,
        selectedIds: selectedDistrictIds,
        key: 'districts' as const,
        callback: onDistrictToggle,
      },
      {
        data: propertyTypes,
        selectedIds: selectedPropertyTypeIds,
        key: 'accommodation_types' as const,
        callback: onPropertyTypeToggle,
      },
      {
        data: rateAmenities,
        selectedIds: selectedRateAmenityIds,
        key: 'free_amenities' as const,
        callback: onRateAmenityToggle,
      },
      {
        data: chains,
        selectedIds: selectedChainIds,
        key: 'chains' as const,
        callback: onChainToggle,
      },
      {
        data: brands,
        selectedIds: selectedBrandIds,
        key: 'brands' as const,
        callback: onBrandToggle,
      },
    ];

    const regularFilters = filterMappings.flatMap(({ data, selectedIds, key, callback }) =>
      (data as any[])
        .filter((item: any) => selectedIds.includes(item.id))
        .map((item: any) => ({ ...item, key, callback })),
    );

    const reviewerGroupFilters = reviewerGroups
      .filter((item: any) => selectedReviewerGroups.includes(item.name))
      .map((item: any) => ({
        ...item,
        key: 'guest_rating' as const,
        callback: onReviewerGroupToggle,
      }));

    return [...regularFilters, ...reviewerGroupFilters];
  }, [
    reviewScoreRanges,
    stars,
    amenities,
    districts,
    propertyTypes,
    reviewerGroups,
    rateAmenities,
    chains,
    brands,
    selectedRangeIds,
    selectedStarIds,
    selectedAmenityIds,
    selectedDistrictIds,
    selectedPropertyTypeIds,
    selectedReviewerGroups,
    selectedRateAmenityIds,
    selectedChainIds,
    selectedBrandIds,
    onReviewScoreToggle,
    onStarToggle,
    onAmenityToggle,
    onDistrictToggle,
    onPropertyTypeToggle,
    onReviewerGroupToggle,
    onRateAmenityToggle,
    onChainToggle,
    onBrandToggle,
  ]);

  return {
    minUsdPrice,
    maxUsdPrice,
    selectedUsdPrices,
    selectedStarIds,
    selectedAmenityIds,
    selectedDistrictIds,
    selectedPropertyTypeIds,
    selectedReviewerGroups,
    selectedRateAmenityIds,
    selectedChainIds,
    selectedBrandIds,
    selectedFilters,

    onUsdPricesChanged,
    onStarToggle,
    onAmenityToggle,
    onDistrictToggle,
    onPropertyTypeToggle,
    onReviewerGroupToggle,
    onRateAmenityToggle,
    onBrandToggle,
    onChainToggle,
  };
};
